import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { CaseSize, FormFactor, UnitSystem } from '../types';

interface FilterState {
  sizeCategories: CaseSize[];
  formFactors: FormFactor[];
  manufacturers: string[];
  minGpuLength: number;
  minCoolerHeight: number;
  minPsuLength: number;
  minRadiatorSize: number;
  sidePanelTypes: string[];
  searchQuery: string;
}

interface ComparisonState {
  selectedCases: string[];
  viewMode: '2d' | '3d';
  overlayMode: boolean;
  showGrid: boolean;
  referenceObject: string | null;
  unitSystem: UnitSystem;
}

interface AppState {
  // Filter state
  filters: FilterState;
  setFilters: (filters: Partial<FilterState>) => void;
  resetFilters: () => void;
  toggleSizeCategory: (size: CaseSize) => void;
  toggleFormFactor: (ff: FormFactor) => void;
  toggleManufacturer: (mfr: string) => void;
  toggleSidePanelType: (type: string) => void;
  setMinGpuLength: (length: number) => void;
  setMinCoolerHeight: (height: number) => void;
  setMinPsuLength: (length: number) => void;
  setMinRadiatorSize: (size: number) => void;
  setSearchQuery: (query: string) => void;

  // Comparison state
  comparison: ComparisonState;
  addToComparison: (caseId: string) => void;
  removeFromComparison: (caseId: string) => void;
  clearComparison: () => void;
  setViewMode: (mode: '2d' | '3d') => void;
  toggleOverlayMode: () => void;
  toggleGrid: () => void;
  setReferenceObject: (id: string | null) => void;
  setUnitSystem: (system: UnitSystem) => void;
  isInComparison: (caseId: string) => boolean;

  // UI State
  filterPanelOpen: boolean;
  setFilterPanelOpen: (open: boolean) => void;
}

const defaultFilters: FilterState = {
  sizeCategories: [],
  formFactors: [],
  manufacturers: [],
  minGpuLength: 0,
  minCoolerHeight: 0,
  minPsuLength: 0,
  minRadiatorSize: 0,
  sidePanelTypes: [],
  searchQuery: '',
};

const defaultComparison: ComparisonState = {
  selectedCases: [],
  viewMode: '2d',
  overlayMode: false,
  showGrid: true,
  referenceObject: null,
  unitSystem: 'metric',
};

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      // Filter state
      filters: defaultFilters,

      setFilters: (newFilters) =>
        set((state) => ({
          filters: { ...state.filters, ...newFilters },
        })),

      resetFilters: () =>
        set({ filters: defaultFilters }),

      toggleSizeCategory: (size) =>
        set((state) => {
          const current = state.filters.sizeCategories;
          const updated = current.includes(size)
            ? current.filter((s) => s !== size)
            : [...current, size];
          return { filters: { ...state.filters, sizeCategories: updated } };
        }),

      toggleFormFactor: (ff) =>
        set((state) => {
          const current = state.filters.formFactors;
          const updated = current.includes(ff)
            ? current.filter((f) => f !== ff)
            : [...current, ff];
          return { filters: { ...state.filters, formFactors: updated } };
        }),

      toggleManufacturer: (mfr) =>
        set((state) => {
          const current = state.filters.manufacturers;
          const updated = current.includes(mfr)
            ? current.filter((m) => m !== mfr)
            : [...current, mfr];
          return { filters: { ...state.filters, manufacturers: updated } };
        }),

      toggleSidePanelType: (type) =>
        set((state) => {
          const current = state.filters.sidePanelTypes;
          const updated = current.includes(type)
            ? current.filter((t) => t !== type)
            : [...current, type];
          return { filters: { ...state.filters, sidePanelTypes: updated } };
        }),

      setMinGpuLength: (length) =>
        set((state) => ({
          filters: { ...state.filters, minGpuLength: length },
        })),

      setMinCoolerHeight: (height) =>
        set((state) => ({
          filters: { ...state.filters, minCoolerHeight: height },
        })),

      setMinPsuLength: (length) =>
        set((state) => ({
          filters: { ...state.filters, minPsuLength: length },
        })),

      setMinRadiatorSize: (size) =>
        set((state) => ({
          filters: { ...state.filters, minRadiatorSize: size },
        })),

      setSearchQuery: (query) =>
        set((state) => ({
          filters: { ...state.filters, searchQuery: query },
        })),

      // Comparison state
      comparison: defaultComparison,

      addToComparison: (caseId) =>
        set((state) => {
          if (state.comparison.selectedCases.includes(caseId)) return state;
          if (state.comparison.selectedCases.length >= 4) return state;
          return {
            comparison: {
              ...state.comparison,
              selectedCases: [...state.comparison.selectedCases, caseId],
            },
          };
        }),

      removeFromComparison: (caseId) =>
        set((state) => ({
          comparison: {
            ...state.comparison,
            selectedCases: state.comparison.selectedCases.filter((id) => id !== caseId),
          },
        })),

      clearComparison: () =>
        set((state) => ({
          comparison: {
            ...state.comparison,
            selectedCases: [],
          },
        })),

      setViewMode: (mode) =>
        set((state) => ({
          comparison: { ...state.comparison, viewMode: mode },
        })),

      toggleOverlayMode: () =>
        set((state) => ({
          comparison: { ...state.comparison, overlayMode: !state.comparison.overlayMode },
        })),

      toggleGrid: () =>
        set((state) => ({
          comparison: { ...state.comparison, showGrid: !state.comparison.showGrid },
        })),

      setReferenceObject: (id) =>
        set((state) => ({
          comparison: { ...state.comparison, referenceObject: id },
        })),

      setUnitSystem: (system) =>
        set((state) => ({
          comparison: { ...state.comparison, unitSystem: system },
        })),

      isInComparison: (caseId) => get().comparison.selectedCases.includes(caseId),

      // UI State
      filterPanelOpen: false,

      setFilterPanelOpen: (open) =>
        set({ filterPanelOpen: open }),
    }),
    {
      name: 'pc-comparer-storage',
      partialize: (state) => ({
        comparison: state.comparison,
        filters: state.filters,
      }),
    }
  )
);
